import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { accountDeleteButtonText, isLinkedAccount, providerSourceDisplayName } from 'src/utils/account';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import RemoveItemDialog from '../views/remove-item-dialog';
import PortfolioAccountDescriptionRow from './portfolio-account-description-row';

export const RemoveAccountDialogFragment = graphql(/* GraphQL */ `
  fragment RemoveAccountDialog on Account {
    ...AccountDeleteButtonText
    ...IsLinkedAccount
    ...PortfolioAccountDescriptionRow
    id
    providerAccount {
      source
    }
  }
`);

const DELETE_ACCOUNT = graphql(/* GraphQL */ `
  mutation DeleteAccount($input: DeleteAccountInput!) {
    deleteAccount(input: $input) {
      success
    }
  }
`);

export default function RemoveAccountDialog({
  accountFrag,
  onRemove,
  onClose,
}: {
  readonly accountFrag: FragmentType<typeof RemoveAccountDialogFragment>;
  readonly onRemove: Deprecated_HandleItemUpdate;
  readonly onClose: () => void;
}) {
  const account = unmaskFragment(RemoveAccountDialogFragment, accountFrag);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteAccount] = useMutationWithErrorLogging(DELETE_ACCOUNT);

  const handleDeleteClick = async () => {
    setIsLoading(true);
    await deleteAccount({
      variables: {
        input: {
          id: account.id,
        },
      },
    });
    onRemove({
      type: UpdateType.DELETE_ACCOUNT,
      id: account.id,
    });
    onClose();
  };

  return (
    <RemoveItemDialog
      descriptionRow={<PortfolioAccountDescriptionRow accountFrag={account} />}
      deleteButtonText={accountDeleteButtonText(account)}
      deletionText={accountDeletionText(account)}
      isLoading={isLoading}
      onClose={onClose}
      onRemove={handleDeleteClick}
    />
  );
}

export function accountDeletionText(account: ResultOf<typeof RemoveAccountDialogFragment>) {
  if (isLinkedAccount(account)) {
    const provider = providerSourceDisplayName(account.providerAccount?.source);
    return `When you remove an account it will no longer appear in your list of accounts and assets and we won't get updates from ${provider}. Historical values of this account will still contribute to your net worth graph.`;
  }
  return `When you delete an account it will no longer appear in your list of accounts, assets, and liabilities. Historical values of this account will be removed from your net worth graph.`;
}
